import React, { FormEvent } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { HsmContact, HsmContactBatch } from '../../types';

export interface ContactFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedContact: HsmContact | null;
  batches: HsmContactBatch[];
  saving: boolean;
  // Form field values
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  name: string;
  setName: (value: string) => void;
  company: string;
  setCompany: (value: string) => void;
  cpfCnpj: string;
  setCpfCnpj: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  batchId: string;
  setBatchId: (value: string) => void;
  // Handlers
  onSubmitCreate: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  onSubmitUpdate: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}

export function ContactFormDialog({
  open,
  onOpenChange,
  selectedContact,
  batches,
  saving,
  phoneNumber,
  setPhoneNumber,
  name,
  setName,
  company,
  setCompany,
  cpfCnpj,
  setCpfCnpj,
  email,
  setEmail,
  batchId,
  setBatchId,
  onSubmitCreate,
  onSubmitUpdate,
}: ContactFormDialogProps) {
  const isEditing = selectedContact !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Contato' : 'Novo Contato'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Atualize as informacoes do contato.'
              : 'Adicione um novo contato a campanha.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={isEditing ? onSubmitUpdate : onSubmitCreate}
        >
          <div className="space-y-2">
            <Label htmlFor="contact-phone">Telefone (E.164)</Label>
            <Input
              id="contact-phone"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+5519971723928"
              required
            />
            <p className="text-xs text-muted-foreground">
              Formato: +[codigo pais][DDD][numero]. Ex: +5519971723928
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-name">Nome (opcional)</Label>
            <Input
              id="contact-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome do contato"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="contact-company">Empresa (opcional)</Label>
              <Input
                id="contact-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Empresa"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-cpf">CPF/CNPJ (opcional)</Label>
              <Input
                id="contact-cpf"
                value={cpfCnpj}
                onChange={(e) => setCpfCnpj(e.target.value)}
                placeholder="CPF ou CNPJ"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-email">Email (opcional)</Label>
            <Input
              id="contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@exemplo.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-batch">Lote (opcional)</Label>
            <Select
              value={batchId || 'none'}
              onValueChange={(value: string) => setBatchId(value === 'none' ? '' : value)}
            >
              <SelectTrigger id="contact-batch">
                <SelectValue placeholder="Selecione um lote" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum lote</SelectItem>
                {batches.map((batch) => (
                  <SelectItem key={batch.id} value={batch.id.toString()}>
                    {batch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
